/* Product-card galleries: stable image wells, on-demand photos and manual navigation. */
(() => {
  const galleries = new WeakMap();
  const imageKey = src => {
    let url = new URL(src, location.href);
    // New mockups share the proxy path; their source identifies the photo.
    // Ignoring the query collapsed every processed variant into one slide.
    if (url.pathname === '/apps/illegal-cutouts/image' && url.searchParams.has('src')) {
      url = new URL(url.searchParams.get('src'), location.href);
    }
    return url.pathname.replace(/-400\.webp$/, '.webp');
  };

  function init(card) {
    if (galleries.has(card)) return galleries.get(card);
    const data = card.querySelector('[data-card-gallery-images]');
    const primary = card.querySelector('.product-card-img');
    if (!data || !primary) return null;
    let images;
    try { images = JSON.parse(data.textContent); } catch (_) { return null; }
    const seen = new Set();
    images = images.filter(image => {
      if (!image.src || seen.has(imageKey(image.src))) return false;
      seen.add(imageKey(image.src));
      return true;
    });
    if (!images.length) return null;
    const modelIndex = document.body.classList.contains('template-index') ? images.findIndex(photo => photo.model) : -1;
    if (modelIndex > 0) {
      images.unshift(images.splice(modelIndex, 1)[0]);
      void window.StorefrontReliability.swapImage(primary, images[0].src, { alt: images[0].alt });
      primary.classList.add('is-model-cutout');
    }
    const media = card.querySelector('.product-card-media');
    const link = card.querySelector('.product-card-link');
    const progress = card.querySelector('.card-gallery-progress');
    const status = card.querySelector('.card-gallery-status');
    const state = { images, index: 0, requested: 0, version: 0, displayed: null, hoverReturn: null, layers: new Set([primary]), fades: new Map() };
    galleries.set(card, state);
    card.classList.toggle('has-card-gallery', images.length > 1);
    link.setAttribute('aria-label', card.querySelector('.card-title').textContent.trim());
    media.style.touchAction = 'pan-y pinch-zoom';

    const mobile = matchMedia('(max-width:749px), (hover:none), (pointer:coarse)');
    let rail;
    function prepareRail() {
      if (!rail) {
        rail = document.createElement('div');
        rail.className = 'card-mobile-rail';
        link.append(rail);
        card.classList.add('has-mobile-rail');
        rail.addEventListener('scroll', () => {
          if (!mobile.matches || !rail.clientWidth) return;
          const index = Math.round(rail.scrollLeft / rail.clientWidth);
          state.index = state.requested = Math.min(state.images.length - 1, Math.max(0, index));
          update();
          status.textContent = `Image ${state.index + 1} of ${state.images.length}`;
          const selectedImage = rail.querySelectorAll('img')[state.index];
          if (selectedImage) window.StorefrontReliability.imageStatus(selectedImage, selectedImage.dataset.imageState || 'loading');
        }, { passive: true });
        new ResizeObserver(() => {
          if (rail.clientWidth && !rail.dataset.swiping) rail.scrollLeft = state.index * rail.clientWidth;
        }).observe(rail);
      }
      state.images.slice(rail.querySelectorAll('img').length).forEach((photo, offset) => {
        const image = new Image();
        image.className = 'shirt-image' + (photo.model ? ' is-model-cutout' : '');
        image.alt = photo.alt;
        image.width = 600;
        image.height = 750;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.src = primary.currentSrc || primary.src;
        rail.append(image);
        const observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          observer.disconnect();
          void window.StorefrontReliability.swapImage(image, photo.src, { alt: photo.alt, fallback: photo.original_src });
        }, { root: rail, rootMargin: '0px 100%' });
        observer.observe(image);
      });
      window.IllegalGallerySwipe?.bind(rail);
      return rail;
    }

    function update() {
      card.dataset.galleryIndex = String(state.index);
      card.dataset.galleryCount = String(state.images.length);
      progress.style.width = `${100 / state.images.length}%`;
      progress.style.transform = `translateX(${state.index * 100}%)`;
    }
    // Keep both decoded photos in place during the fade. Reversing a hover
    // continues from the visible opacity instead of snapping back to an endpoint.
    function reveal(next) {
      const incoming = next || primary;
      if (!state.layers.has(incoming)) {
        incoming.style.opacity = '0';
        link.append(incoming);
        state.layers.add(incoming);
      }
      state.displayed = next;
      const reduced = matchMedia('(prefers-reduced-motion:reduce)').matches;
      state.layers.forEach(layer => {
        const from = layer.style.visibility === 'hidden' ? 0 : Number(getComputedStyle(layer).opacity);
        const previous = state.fades.get(layer);
        if (previous) {
          previous.onfinish = null;
          previous.cancel();
          state.fades.delete(layer);
        }
        const target = layer === incoming ? 1 : 0;
        layer.style.visibility = '';
        layer.style.opacity = String(target);
        if (target) layer.removeAttribute('aria-hidden');
        else layer.setAttribute('aria-hidden', 'true');
        const finish = () => {
          state.fades.delete(layer);
          if (target || layer === (state.displayed || primary)) return;
          if (layer === primary) layer.style.visibility = 'hidden';
          else { layer.remove(); state.layers.delete(layer); }
        };
        if (reduced || !layer.animate || Math.abs(target - from) < .01) { finish(); return; }
        const fade = layer.animate([{ opacity: from }, { opacity: target }], {
          duration: Math.max(140, 380 * Math.abs(target - from)),
          easing: 'cubic-bezier(.4,0,.2,1)'
        });
        state.fades.set(layer, fade);
        fade.onfinish = finish;
      });
    }
    state.show = async (index, announce = true) => {
      const nextIndex = (index + state.images.length) % state.images.length;
      const version = ++state.version;
      state.requested = nextIndex;
      if (mobile.matches) {
        const mobileRail = prepareRail();
        state.index = nextIndex;
        mobileRail.scrollLeft = nextIndex * mobileRail.clientWidth;
        update();
        if (announce) status.textContent = `Image ${state.index + 1} of ${state.images.length}`;
        return;
      }
      const photo = state.images[nextIndex];
      let next = null;
      media.setAttribute('aria-busy', 'true');
      if (nextIndex !== 0 || imageKey(primary.currentSrc || primary.src) !== imageKey(photo.src)) {
        next = [...state.layers].find(layer => layer !== primary && imageKey(layer.src) === imageKey(photo.src)) || new Image();
        next.className = 'shirt-image card-gallery-image' + (photo.model ? ' is-model-cutout' : '');
        next.alt = photo.alt;
        next.width = 600;
        next.height = 750;
        try {
          let loaded;
          try { loaded = await window.StorefrontReliability.loadImage(photo.src); }
          catch (error) {
            const original = window.StorefrontReliability.originalSource(photo.original_src);
            if (!original || original === photo.src) throw error;
            loaded = await window.StorefrontReliability.loadImage(original);
          }
          next.src = loaded.currentSrc || loaded.src;
        } catch (_) {
          if (version === state.version) {
            state.requested = state.index;
            media.removeAttribute('aria-busy');
            if (announce) status.textContent = 'This image could not load. Please try again.';
          }
          return;
        }
      }
      if (version !== state.version) return;
      reveal(next);
      state.index = nextIndex;
      media.removeAttribute('aria-busy');
      update();
      if (announce) status.textContent = `Image ${state.index + 1} of ${state.images.length}`;
    };
    state.navigate = step => {
      state.hoverReturn = null;
      state.show(state.requested + step);
    };
    const modelColor = card.querySelector('.card-swatch.is-active')?.dataset.color;
    card.addEventListener('mouseenter', () => {
      if (!matchMedia('(hover:hover) and (pointer:fine) and (min-width:750px)').matches || state.index !== 0) return;
      if (card.dataset.selectedColor && card.dataset.selectedColor !== modelColor) return;
      const modelIndex = state.images.findIndex(photo => photo.model);
      if (modelIndex < 1) return;
      state.hoverReturn = state.index;
      state.show(modelIndex, false);
    });
    card.addEventListener('mouseleave', () => {
      if (state.hoverReturn === null) return;
      const restore = state.hoverReturn;
      state.hoverReturn = null;
      state.show(restore, false);
    });
    card.addEventListener('card:color-image', event => {
      state.hoverReturn = null;
      primary.classList.remove('is-model-cutout');
      const photo = { ...event.detail, model: false };
      let index = state.images.findIndex(image => imageKey(image.src) === imageKey(photo.src));
      if (index < 0) { index = state.images.length; state.images.push(photo); }
      card.classList.toggle('has-card-gallery', state.images.length > 1);
      state.show(index);
    });
    update();
    if (mobile.matches) prepareRail();
    mobile.addEventListener('change', () => state.show(state.index, false));
    return state;
  }

  function scan(root) {
    if (root.nodeType !== 1 && root !== document) return;
    if (root.matches?.('.product-card')) init(root);
    root.querySelectorAll('.product-card').forEach(init);
  }
  scan(document);
  new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(scan));
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-card-gallery-step]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const gallery = init(button.closest('.product-card'));
    if (gallery) gallery.navigate(Number(button.dataset.cardGalleryStep));
  });
  document.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const media = event.target.closest('.product-card-media');
    if (!media || event.target.closest('.quick-add-trigger')) return;
    const gallery = init(media.closest('.product-card'));
    if (!gallery || gallery.images.length < 2) return;
    event.preventDefault();
    gallery.navigate(event.key === 'ArrowRight' ? 1 : -1);
  });

})();
